import { z } from 'zod';

// ============================================================================
// Pricing Schedules
// ============================================================================

export const PRICING_SCHEDULE_RULE_TYPES = ['HOLIDAY', 'DATE_RANGE', 'DAY_OF_WEEK'] as const;
export type PricingScheduleRuleType = (typeof PRICING_SCHEDULE_RULE_TYPES)[number];

export const PRICING_SCHEDULE_STATUSES = ['DRAFT', 'ACTIVE', 'ARCHIVED'] as const;
export type PricingScheduleStatus = (typeof PRICING_SCHEDULE_STATUSES)[number];

export const DateRangeSchema = z.object({
  from: z.string().date(),
  to: z.string().date(),
});

export type DateRange = z.infer<typeof DateRangeSchema>;

export const PricingScheduleRuleSchema = z.object({
  label: z.string().min(1, 'label is required'),
  price: z.number().int().min(0).optional(),
  percentOverride: z.number().min(0).optional(),
  amountOverride: z.number().int().min(0).optional(),
  priority: z.number().int(),
  ruleType: z.enum(PRICING_SCHEDULE_RULE_TYPES),
  daysOfWeek: z.array(z.number().int().min(0).max(6)).optional(),
  dateRanges: z.array(DateRangeSchema).optional(),
});

export type PricingScheduleRule = z.infer<typeof PricingScheduleRuleSchema>;

export const CreatePricingScheduleRequestSchema = z.object({
  name: z.string().min(1, 'name is required'),
  description: z.string().optional(),
  fallbackPrice: z.number().int().min(0),
  validFrom: z.string().date().optional(),
  validUntil: z.string().date().optional(),
  rules: z.array(PricingScheduleRuleSchema).optional(),
  createdBy: z.string().optional(),
});

export type CreatePricingScheduleRequest = z.infer<typeof CreatePricingScheduleRequestSchema>;

export const UpdatePricingScheduleRequestSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  fallbackPrice: z.number().int().min(0).optional(),
  validFrom: z.string().date().optional(),
  validUntil: z.string().date().optional(),
  rules: z.array(PricingScheduleRuleSchema).optional(),
  updatedBy: z.string().optional(),
});

export type UpdatePricingScheduleRequest = z.infer<typeof UpdatePricingScheduleRequestSchema>;

export const ListSchedulesQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  status: z.enum(PRICING_SCHEDULE_STATUSES).optional(),
  search: z.string().optional(),
});

export type ListSchedulesQuery = z.infer<typeof ListSchedulesQuerySchema>;

export const SchedulePreviewQuerySchema = z.object({
  date: z.string().date(),
});

export type SchedulePreviewQuery = z.infer<typeof SchedulePreviewQuerySchema>;

// ============================================================================
// Response schemas — wire format returned by the controller
// ============================================================================

// GET /admin/pricing/schedules/:id  (and mutations: create, update, activate, archive)
export const PricingScheduleRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(PRICING_SCHEDULE_STATUSES),
  validFrom: z.string().date().optional(),
  validUntil: z.string().date().optional(),
  fallbackPrice: z.number().int().nonnegative(),
  rules: z.array(PricingScheduleRuleSchema),
  createdBy: z.string().nullable(),
  updatedBy: z.string().nullable(),
  deletedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  _count: z.object({ ticketTypes: z.number().int() }).optional(),
});

export type PricingScheduleRecord = z.infer<typeof PricingScheduleRecordSchema>;

// GET /admin/pricing/schedules  (paginated list)
export const PricingScheduleListResponseSchema = z.object({
  schedules: z.array(PricingScheduleRecordSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});

export type PricingScheduleListResponse = z.infer<typeof PricingScheduleListResponseSchema>;

// GET /admin/pricing/schedules/:id/preview
export const SchedulePreviewResultSchema = z.object({
  date: z.string(),
  scheduleId: z.string(),
  scheduleName: z.string(),
  price: z.number().int().nonnegative(),
  source: z.enum(['HOLIDAY', 'DATE_RANGE', 'DAY_OF_WEEK', 'SCHEDULE_FALLBACK']),
  label: z.string(),
});

export type SchedulePreviewResult = z.infer<typeof SchedulePreviewResultSchema>;

// GET /admin/pricing/schedules/options
export const PricingScheduleOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(PRICING_SCHEDULE_STATUSES),
});

export type PricingScheduleOption = z.infer<typeof PricingScheduleOptionSchema>;
